import { Component, EventEmitter, Input, OnInit, Output } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { NgbPopoverConfig, NgbPopoverModule } from "@ng-bootstrap/ng-bootstrap";

import { DataService } from "src/app/core/core-services/services/dataservices/data.service";
import { DialogService } from "src/app/core/core-services/services/dialog.service";
import { ExtensionService } from "src/app/core/core-services/services/extension.service";
import { ListService } from "src/app/core/core-services/services/list.service";
import { NavigationService } from "src/app/core/core-services/services/navigation.service";
import { NotificationService } from "src/app/core/core-services/services/notification.service";
import { ScrollService } from "src/app/core/core-services/services/scroll.service";
import { modalStepIndex } from "src/app/core/core-services/services/onboarding-progress.service";

import { ApplicationConstants } from "src/app/core/core-services/constants/application.constants";

import { MetaService } from "src/app/core/core-services/services/meta.service";
import {
  CategoryConstant,
  getPlanId,
  getRewards,
  RewardsConstants,
} from "../../core/core-services/constants/plans.constants";
import { WarningDialogComponent } from "../../core/core-services/warning-dialog/warning-dialog.component";
import { TemplateCategoryCopyComponent } from "./template-category-copy/template-category-copy.component";
import { TemplateCopyComponent } from "./template-copy/template-copy.component";
import { GlobalHookService } from "src/app/global-hook.service";
import { CommonModule } from "@angular/common";
import { InfiniteScrollModule } from "ngx-infinite-scroll";
import { DownloadBannerComponent } from "src/app/download-banner/download-banner.component";
import { FormsModule } from "@angular/forms";
import { EditorModule } from "@tinymce/tinymce-angular";
import { HighlighterPipe } from "src/app/core/core-services/highlighter/highlighter.pipe";
import { SvgIconsModule } from "src/app/module/svg.module";
import { NgxSkeletonLoaderModule } from "ngx-skeleton-loader";
import {
  VngIconComponent,
  VngButtonComponent,
  VngExpansionPanelComponent,
  VngTourAnchorDirective,
  VngTourService,
} from "@vengreso/angular-components";
import { FlymsgRowHeaderComponent } from "src/app/vengreso/shared/flymsg-row/flymsg-row-header/flymsg-row-header.component";
import { FlymsgRowEditorComponent } from "src/app/vengreso/shared/flymsg-row/flymsg-row-editor/flymsg-row-editor.component";
import { FlyPlate } from "src/app/core/models/flymsg.model";

// C3-9: this component is standalone, so its own `imports:` array is private —
// removing the now-fully-migrated Mat*Module entries here carries none of the
// shared-module merge-conflict risk the §4 only-add rule guards against
// (nothing else compiles against TemplatesComponent's decorator), same as
// C3-4's ShortcutsComponent.
@Component({
    selector: "app-templates",
    templateUrl: "./templates.component.html",
    styleUrls: ["./templates.component.scss"],
    imports: [
        CommonModule,
        InfiniteScrollModule,
        DownloadBannerComponent,
        VngIconComponent,
        NgbPopoverModule,
        VngButtonComponent,
        SvgIconsModule,
        VngExpansionPanelComponent,
        FormsModule,
        EditorModule,
        HighlighterPipe,
        NgxSkeletonLoaderModule,
        VngTourAnchorDirective,
        FlymsgRowHeaderComponent,
        FlymsgRowEditorComponent,
    ]
})
export class TemplatesComponent implements OnInit {
  @Input() searchPlates: FlyPlate[];
  @Input() fromSearch: any;
  @Input() downScroll: boolean;
  @Input() searchValue: string;
  @Output("detectUpdates") detectUpdates: EventEmitter<any> = new EventEmitter();

  categoryId: string;
  currentcategory: string;
  templateList: FlyPlate[];
  editorConfig: any;
  panelOpenState = {};
  editorApiKey: string;
  scrollDisabled = true;
  searchText = "";
  initialLoading = true;

  constructor(
    config: NgbPopoverConfig,
    private route: ActivatedRoute,
    private dialog: DialogService,
    public nav: NavigationService,
    public listService: ListService,
    private dataService: DataService,
    public scrollService: ScrollService,
    private notification: NotificationService,
    private extensionService: ExtensionService,
    private metaService: MetaService,
    private readonly tourService: VngTourService,
    private readonly globalHookService: GlobalHookService
  ) {
    config.triggers = "hover";
    config.container = "body";
  }

  ngOnInit() {
    this.templateList = [];
    setTimeout(() => {
      const isGrowthPlan = getPlanId() === "Growth";
      this.scrollDisabled = false;
      this.editorApiKey = ApplicationConstants.TINYMCE_API_KEY;
      this.editorConfig = {
        statusbar: false,
        menubar: false,
        toolbar: false,
        min_height: 100,
        max_height: 200,
        // Autoresize caps the editor shell but leaves the iframe body as tall as its content,
        // so premium previews need a fixed viewport for body overflow to create a scroll range.
        ...(isGrowthPlan ? { plugins: ["autoresize"] } : { height: 200 }),
        body_class: "noselect",
        content_style:
          !isGrowthPlan
            ? "html {height: 100%; overflow: hidden;} body {box-sizing: border-box; height: 100%; margin: 0; padding: 1rem; position: relative; background: #F5F5F5; opacity: 0.5; overflow-y: auto; -webkit-touch-callout: none; -webkit-user-select: none; -khtml-user-select: none; -moz-user-select: none; -ms-user-select: none; user-select: none; font-family: Arial;} body::after {margin: 3rem;content: 'PREMIUM';position: absolute;top: 0;z-index: -1; font-size: 4rem; text-align: center;font-style: normal;top: -1.5rem;font-weight: 800;font-size: 4rem; width: 100%; height: ; left: -3%;line-height: 107px;letter-spacing: 1em; opacity: 0.23; color: #000000; }"
            : "",
      };
    }, 0);
    if (!this.fromSearch) {
      this.route.data.subscribe(
        (data) => {
          const categoryName = this.route.snapshot.paramMap.get("categoryName");
          const routeCategoryId = this.route.snapshot.paramMap.get("categoryId");

          if ((data.templateList && data.templateList.result) || this.fromSearch) {
            this.initialLoading = true;
            const callback = () => {
              if (this.isOnboarding) {
                setTimeout(() => {
                  const tf = this.templateList.find((f) => f.shortcut === "-tf");
                  // C3-9: vng-expansion-panel has no imperative `.open()` API
                  // (unlike MatExpansionPanel's ViewChildren-driven `.open()`
                  // used pre-migration to force-expand the tour's target
                  // panel). Reuse the same panelOpenState flag that already
                  // gates the lazy-rendered editor as the panel's `[expanded]`
                  // binding (templates.component.html) — setting it true here
                  // now also visually expands the panel on the next CD tick.
                  this.panelOpenState[tf?.id] = true;

                  this.tourService.initialize([
                    {
                      anchorId: "step17" + tf?.id,
                      content: "Click + Add to FlyCuts to add this FlyPlate to your library of messages. ",
                      title: "Add a FlyPlate",
                      enableBackdrop: true,
                      disablePageScrolling: true,
                      nextOnAnchorClick: true,
                      placement: {
                        xPosition: "before",
                        yPosition: "above",
                      },
                      // `fms-tour-hide-end` removes the "Got it" button. It could
                      // only dismiss the step — it does not open the Add To
                      // FlyCuts dialog, so anyone who took it was left mid-tour
                      // with nothing to continue into. Master hid it the same
                      // way (`document.body.classList.add("tour-last-step")`,
                      // whose rule dropped the whole action row). This is a
                      // single-step tour, so there is no Back button either: the
                      // only way on is the "+ Add To FlyCuts" button the copy is
                      // pointing at, which ends the tour and opens the dialog.
                      // Caret is bottom-end to match `{before, above}`.
                      popoverClass: "fms-tour-arrow fms-tour-arrow--bottom-end fms-tour-hide-end",
                    },
                  ]);

                  setTimeout(() => {
                    this.tourService.start();
                  }, 1000);
                }, 50);
              }

              setTimeout(() => {
                this.initialLoading = false;
              }, 10);
            };

            let config: any = {
              self: this,
              listName: "templateList",
              scrollContainer: ".app-inner",
              url:
                "v1/user/template?order_by=shortcut,asc" +
                (!routeCategoryId || routeCategoryId.toLowerCase() === "all"
                  ? ""
                  : "&category_id=" + routeCategoryId) +
                "&",
              callback,
            };

            if (categoryName) {
              const subCategoryName = this.route.snapshot.paramMap.get("subCategoryName");
              config = {
                self: this,
                listName: "templateList",
                url:
                  "v1/user/templatebyname?order_by=shortcut,asc" +
                  (categoryName.toLowerCase() === "viewall" ? "" : "&category_name=" + categoryName) +
                  (subCategoryName ? "&sub_category_name=" + subCategoryName : "") +
                  "&",
                callback,
              };

              if (subCategoryName) {
                const subSubCategoryName = this.route.snapshot.paramMap.get("subSubCategoryName");
                config = {
                  self: this,
                  listName: "templateList",
                  url:
                    "v1/user/templatebyname?order_by=shortcut,asc" +
                    (categoryName.toLowerCase() === "viewall" ? "" : "&category_name=" + categoryName) +
                    ("&sub_category_name=" + subCategoryName) +
                    (subSubCategoryName ? "&sub_sub_category_name=" + subSubCategoryName : "") +
                    "&",
                  callback,
                };
              }
            }

            this.listService.init(config, data.templateList.result);
            this.categoryId =
              routeCategoryId || this.templateList.find((template) => !!template.category_id)?.category_id;
          }
        },
        () => {
          this.initialLoading = false;
        }
      );
    }
    this.metaService.addMetaTag(
      "FlyPlates View all Category - Templates",
      "FlyPlates is a repository of over 200 templates you can choose from. What's your time worth?"
    );
    if (this.fromSearch) {
      this.basicSearchHandle();
      this.searchText = this.searchValue;
    }

    this.route.paramMap.subscribe((params) => {
      this.currentcategory = params.get("categoryName") || "viewall";
    });
  }

  get isOnboarding() {
    return this.globalHookService.onboardingActive;
  }

  ngOnChanges() {
    if (this.fromSearch) {
      this.basicSearchHandle();
    }
  }

  basicSearchHandle() {
    this.templateList = this.searchPlates;
    if (this.templateList) {
      const config = {
        self: this,
        listName: "templateList",
        scrollContainer: ".app-inner",
        url:
          "v1/user/template?order_by=shortcut,asc" +
          (this.categoryId === "all" ? "" : "&category_id=" + this.categoryId) +
          "&",
      };

      this.listService.init(config, this.templateList);
    }
    this.initialLoading = false;
  }

  flycutExistsSearch(data: FlyPlate, disableClose: boolean = false): any {
    this.dataService.flycutExistsSearch({ q: data.shortcut }).subscribe((response: any) => {
      if (response?.result) {
        if (response.result.data.exists) {
          this.dialog.openDialogComponent(
            WarningDialogComponent,
            {
              errorMessage: `The FlyCut`,
              errorMessage2: `is being used on another message. Please change your FlyCut and try again`,
              flycut: data.shortcut,
            },
            "407px"
          );
        } else {
          this.templateCopyToShortcut(data, disableClose);
        }
      }
    });
  }

  addTemplateToShortcut(data: FlyPlate) {
    this.flycutExistsSearch(data, this.isOnboarding);

    if (this.isOnboarding) {
      this.tourService.end();
    }
  }

  templateCopyToShortcut(data: FlyPlate, disableClose: boolean = false) {
    let planId = getPlanId();
    let rewards = getRewards();
    if (!rewards.length && !rewards.includes(RewardsConstants.ALL_FLYPLATES)) {
      if (planId === "Free" && data?.premium) {
        this.dialog.openDialogComponent(
          WarningDialogComponent,
          { self: this, type: "Flyplates", showTheStarter: planId === "Free" },
          "800px"
        );
        return;
      }
    }

    const ref = this.dialog.openDialogComponent(TemplateCopyComponent, { self: this, data }, "400px", disableClose);

    ref.afterClosed().subscribe((result) => {
      if (this.isOnboarding) {
        // Reopen on the NEXT step (Create a Persona) — issue #542.
        this.globalHookService.components.global.startModal(modalStepIndex("create_persona"));
      }
    });
  }

  templateCategoryCopyToShortcut() {
    let planId = getPlanId();
    let rewards = getRewards();

    if (!rewards.includes(RewardsConstants.ALL_FLYPLATES)) {
      if (planId === "Free" && CategoryConstant.GENERAL != this.currentcategory) {
        this.dialog.openDialogComponent(
          WarningDialogComponent,
          { self: this, type: "Flyplates", showTheStarter: planId === "Free", error: "flyplatesFree" },
          "800px"
        );
        return;
      }
    }

    this.dialog.openDialogComponent(TemplateCategoryCopyComponent, { self: this }, "330px");
  }

  resetTemplate(data: FlyPlate, isReset = true) {
    const confirmData = isReset
      ? {
          title: "Resetting this FlyPlate will restore the original version removing any changes you've previously made.",
          message: "Would you still want to reset it?",
          confirmLabel: "Reset",
        }
      : {
          title: "Do you want to update your FlyCut with this updated version from FlyPlates?",
          message: "NOTE - This will override whatever you have currently in your FlyCut.",
          confirmLabel: "Update",
        };

    this.dialog.confirm(confirmData).subscribe((confirmed) => {
      if (confirmed) {
        this.update(data);
      }
    });
  }

  update(data: FlyPlate) {
    const params = {
      template_id: data.id,
    };
    this.dataService.put({ url: "v1/user/copy-shortcut/template/single", data: params }).subscribe({
      next: (response: any) => {
        if (response && response.result) {
          this.notification.toast("FlyCut updated successfully.");
          this.listService.getList(true);
          this.extensionService.sendUpdateMessage();
          this.detectUpdates?.emit();
        }
      },
      // DataService has already surfaced the server's error. Keeping the row
      // unchanged avoids a false success when a catalog item disappeared.
      error: () => {},
    });
  }
}
